/**
 * FAQ Hub Web Component
 * Handles sidebar navigation, search functionality, category switching, and URL hash linking
 */
class FaqHub extends HTMLElement {
  constructor() {
    super();

    this.searchInput = this.querySelector('.faq-hub__search-input');
    this.searchBtn = this.querySelector('.faq-hub__search-btn');
    this.clearSearchBtn = this.querySelector('.faq-hub__clear-search');
    this.searchResultsHeader = this.querySelector('.faq-hub__search-results-header');
    this.resultsCount = this.querySelector('.faq-hub__results-count');
    this.noResults = this.querySelector('.faq-hub__no-results');
    this.navItems = this.querySelectorAll('.faq-hub__nav-item');
    this.panels = this.querySelectorAll('.faq-hub__panel');
    this.questions = this.querySelectorAll('.faq-hub__question');
    this.mobileQuestions = this.querySelectorAll('.faq-hub__mobile-question');
    this.desktopLayout = this.querySelector('.faq-hub__desktop-layout');
    this.sidebar = this.querySelector('.faq-hub__sidebar');
    this.mainContent = this.querySelector('.faq-hub__main');
    this.mobileSections = this.querySelectorAll('.faq-hub__mobile-section');

    this.searchMode = false;
    this.searchDebounceTimer = null;

    // Build slug mapping for friendly URLs
    this.buildSlugMap();

    this.init();
  }

  buildSlugMap() {
    // Map friendly slugs to category IDs
    this.slugMap = {};
    this.navItems.forEach((item) => {
      const categoryId = item.dataset.categoryId;
      const label = item.querySelector('.faq-hub__nav-label')?.textContent || '';
      // Create slug from label: "Orders & Purchases" -> "orders-purchases"
      const slug = label
        .toLowerCase()
        .replace(/&/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      if (slug) {
        this.slugMap[slug] = categoryId;
      }
      // Also map the raw category ID (without "category_" prefix if present)
      const shortId = categoryId.replace(/^category_/, '');
      this.slugMap[shortId] = categoryId;
      this.slugMap[categoryId] = categoryId;
    });
  }

  init() {
    // Sidebar navigation
    this.navItems.forEach((item) => {
      item.addEventListener('click', () => this.handleNavClick(item, true));
    });

    // Search functionality
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.handleSearchInput());
      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.performSearch();
        }
      });
    }

    if (this.searchBtn) {
      this.searchBtn.addEventListener('click', () => this.performSearch());
    }

    if (this.clearSearchBtn) {
      this.clearSearchBtn.addEventListener('click', () => this.clearSearch());
    }

    // Handle initial URL hash
    this.handleHashNavigation();

    // Listen for hash changes (back/forward navigation)
    window.addEventListener('hashchange', () => this.handleHashNavigation());
  }

  handleHashNavigation() {
    const hash = window.location.hash.slice(1); // Remove '#'
    if (!hash) return;

    // Look up category ID from slug map
    const categoryId = this.slugMap[hash] || this.slugMap[hash.toLowerCase()];
    if (!categoryId) return;

    // Find the nav item for this category
    const navItem = this.querySelector(`.faq-hub__nav-item[data-category-id="${categoryId}"]`);
    if (navItem) {
      this.handleNavClick(navItem, false); // Don't update URL since we're reading from it

      // On mobile, also open the corresponding accordion section
      const mobileSection = this.querySelector(`.faq-hub__mobile-section[data-category-id="${categoryId}"]`);
      if (mobileSection) {
        const details = mobileSection.querySelector('.faq-hub__mobile-section-details');
        if (details) {
          // Close other sections first
          this.mobileSections.forEach((section) => {
            const otherDetails = section.querySelector('.faq-hub__mobile-section-details');
            if (otherDetails && section !== mobileSection) {
              otherDetails.removeAttribute('open');
            }
          });
          details.setAttribute('open', '');
        }
        // Scroll to section after a brief delay for DOM update
        setTimeout(() => {
          mobileSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }

  getSlugFromCategoryId(categoryId) {
    // Find the nav item and generate slug from its label
    const navItem = this.querySelector(`.faq-hub__nav-item[data-category-id="${categoryId}"]`);
    const label = navItem?.querySelector('.faq-hub__nav-label')?.textContent || '';
    return label
      .toLowerCase()
      .replace(/&/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  handleNavClick(clickedItem, updateHash = true) {
    const categoryId = clickedItem.dataset.categoryId;

    // Update nav active state
    this.navItems.forEach((item) => {
      item.classList.remove('active');
      item.setAttribute('aria-selected', 'false');
    });
    clickedItem.classList.add('active');
    clickedItem.setAttribute('aria-selected', 'true');

    // Show corresponding panel
    this.panels.forEach((panel) => {
      if (panel.id === `faq-category-${categoryId}`) {
        panel.classList.add('active');
        panel.removeAttribute('hidden');
      } else {
        panel.classList.remove('active');
        panel.setAttribute('hidden', '');
      }
    });

    // Update URL hash for shareable links
    if (updateHash) {
      const slug = this.getSlugFromCategoryId(categoryId);
      if (slug) {
        history.replaceState(null, '', `#${slug}`);
      }
    }

    // Scroll to top of main content on mobile
    if (window.innerWidth <= 900) {
      this.mainContent?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  handleSearchInput() {
    // Debounce search
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      const query = this.searchInput.value.trim();
      if (query.length >= 2) {
        this.performSearch();
      } else if (query.length === 0) {
        this.clearSearch();
      }
    }, 300);
  }

  performSearch() {
    const query = this.searchInput.value.trim().toLowerCase();

    if (!query) {
      this.clearSearch();
      return;
    }

    this.searchMode = true;
    this.classList.add('search-mode');

    // Hide sidebar
    if (this.sidebar) {
      this.sidebar.style.display = 'none';
    }

    // Update desktop layout to single column
    if (this.desktopLayout) {
      this.desktopLayout.style.gridTemplateColumns = '1fr';
    }

    // Hide all panels
    this.panels.forEach((panel) => {
      panel.classList.remove('active');
      panel.style.display = 'none';
    });

    // Search through questions
    let matchCount = 0;
    const matchedQuestions = [];

    this.questions.forEach((question) => {
      const questionText = question.dataset.questionText?.toLowerCase() || '';
      const categoryId = question.dataset.categoryId;

      if (questionText.includes(query)) {
        matchCount++;
        question.style.display = 'block';
        question.classList.add('search-result');

        // Add category tag if not present
        this.addCategoryTag(question, categoryId);

        // Highlight matching text
        this.highlightText(question, query);

        matchedQuestions.push(question);
      } else {
        question.style.display = 'none';
        question.classList.remove('search-result');
      }
    });

    // Also filter mobile questions
    this.mobileQuestions.forEach((question) => {
      const questionText = question.dataset.questionText?.toLowerCase() || '';
      if (questionText.includes(query)) {
        question.style.display = 'block';
        this.highlightMobileText(question, query);
      } else {
        question.style.display = 'none';
      }
    });

    // Show search results in a consolidated view
    this.showSearchResults(matchedQuestions, matchCount, query);

    // Update results count
    if (this.resultsCount) {
      this.resultsCount.textContent = `${matchCount} result${matchCount !== 1 ? 's' : ''} for "${query}"`;
    }

    // Show/hide results header and no results message
    if (this.searchResultsHeader) {
      this.searchResultsHeader.style.display = 'flex';
    }

    if (this.noResults) {
      this.noResults.style.display = matchCount === 0 ? 'block' : 'none';
    }
  }

  showSearchResults(questions, count, query) {
    // Create or get search results container
    let resultsContainer = this.querySelector('.faq-hub__search-results');

    if (!resultsContainer) {
      resultsContainer = document.createElement('div');
      resultsContainer.className = 'faq-hub__search-results';
      this.mainContent?.appendChild(resultsContainer);
    }

    // Clear and populate
    resultsContainer.innerHTML = '';
    resultsContainer.style.display = 'block';

    const questionsWrapper = document.createElement('div');
    questionsWrapper.className = 'faq-hub__questions';

    questions.forEach((question) => {
      const clone = question.cloneNode(true);
      clone.style.display = 'block';
      questionsWrapper.appendChild(clone);
    });

    resultsContainer.appendChild(questionsWrapper);
  }

  addCategoryTag(question, categoryId) {
    const header = question.querySelector('.faq-hub__question-header');
    if (!header) return;

    // Remove existing tag
    const existingTag = header.querySelector('.faq-hub__category-tag');
    if (existingTag) existingTag.remove();

    // Find category name
    const navItem = this.querySelector(`.faq-hub__nav-item[data-category-id="${categoryId}"]`);
    const categoryName = navItem?.querySelector('.faq-hub__nav-label')?.textContent || '';

    if (categoryName) {
      const tag = document.createElement('span');
      tag.className = 'faq-hub__category-tag';
      tag.textContent = categoryName;

      const titleWrapper = document.createElement('div');
      titleWrapper.appendChild(tag);

      const title = header.querySelector('.faq-hub__question-title');
      if (title) {
        title.parentNode.insertBefore(titleWrapper, title);
        titleWrapper.appendChild(title);
      }
    }
  }

  highlightText(question, query) {
    const title = question.querySelector('.faq-hub__question-title');
    if (!title) return;

    const originalText = question.dataset.questionText || title.textContent;
    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
    const highlightedText = originalText.replace(regex, '<span class="faq-hub__highlight">$1</span>');

    title.innerHTML = highlightedText;
  }

  highlightMobileText(question, query) {
    const header = question.querySelector('.faq-hub__mobile-question-header span');
    if (!header) return;

    const originalText = question.dataset.questionText || header.textContent;
    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
    const highlightedText = originalText.replace(regex, '<span class="faq-hub__highlight">$1</span>');

    header.innerHTML = highlightedText;
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  clearSearch() {
    this.searchMode = false;
    this.classList.remove('search-mode');

    if (this.searchInput) {
      this.searchInput.value = '';
    }

    // Show sidebar
    if (this.sidebar) {
      this.sidebar.style.display = '';
    }

    // Reset desktop layout
    if (this.desktopLayout) {
      this.desktopLayout.style.gridTemplateColumns = '';
    }

    // Hide search results header
    if (this.searchResultsHeader) {
      this.searchResultsHeader.style.display = 'none';
    }

    // Hide no results message
    if (this.noResults) {
      this.noResults.style.display = 'none';
    }

    // Hide search results container
    const resultsContainer = this.querySelector('.faq-hub__search-results');
    if (resultsContainer) {
      resultsContainer.style.display = 'none';
    }

    // Reset questions
    this.questions.forEach((question) => {
      question.style.display = '';
      question.classList.remove('search-result');

      // Remove category tag
      const tag = question.querySelector('.faq-hub__category-tag');
      if (tag) tag.remove();

      // Reset title to original text
      const title = question.querySelector('.faq-hub__question-title');
      if (title && question.dataset.questionText) {
        title.textContent = question.dataset.questionText;
      }
    });

    // Reset mobile questions
    this.mobileQuestions.forEach((question) => {
      question.style.display = '';

      // Reset text
      const header = question.querySelector('.faq-hub__mobile-question-header span');
      if (header && question.dataset.questionText) {
        header.textContent = question.dataset.questionText;
      }
    });

    // Show first panel
    this.panels.forEach((panel, index) => {
      panel.style.display = '';
      if (index === 0) {
        panel.classList.add('active');
        panel.removeAttribute('hidden');
      } else {
        panel.classList.remove('active');
        panel.setAttribute('hidden', '');
      }
    });

    // Reset nav to first item
    this.navItems.forEach((item, index) => {
      if (index === 0) {
        item.classList.add('active');
        item.setAttribute('aria-selected', 'true');
      } else {
        item.classList.remove('active');
        item.setAttribute('aria-selected', 'false');
      }
    });
  }
}

customElements.define('faq-hub', FaqHub);
